import { Modal, Input, Button, Row, Col, Form } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
/* eslint-disable react/prop-types */
import { updateNtierLabels } from 'modules/benefitsWellness/services/BenGuideService';
import styles from './TierNameEditor.module.less';

type TierNameEditorProps = {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  coverages: {
    title: string;
    name: string;
    items: CoverageType[];
  }[];
  planType?: string;
  masterId?: string;
  nTiersCount?: number;
};

type CoverageType = {
  value: string;
  fourTierValue: string;
  nTierValue: string;
  label: string;
  coverageLabel: string;
};

const TierNameEditor: React.FC<TierNameEditorProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  coverages,
  planType,
  masterId,
  nTiersCount = 7,
}) => {
  const [form] = Form.useForm();
  const [tierNames, setTierNames] = useState<Record<string, string>>({});
  const [isFormValid, setIsFormValid] = useState(true);
  const fieldNames = coverages[0]?.items?.map((item) => item.nTierValue) || [];
  const values = form.getFieldsValue(fieldNames);
  const hasEmpty = fieldNames.some((name) => !values[name]?.trim());

  // Initialize form values when coverages change
  useEffect(() => {
    if (coverages.length > 0 && coverages[0].items) {
      const initialValues: Record<string, string> = {};
      coverages[0].items.forEach((item) => {
        initialValues[item.nTierValue] = item.label;
      });
      setTierNames(initialValues);
      form.setFieldsValue(initialValues);
    }
  }, [coverages, form, isOpen]);

  // Handle EE_PLUS_1 -> EE+1, EE_PLUS_2 -> EE+2, etc.
  const formatTierForDisplay = (tierValue: string): string => {
    if (tierValue.startsWith('EE_PLUS_')) {
      const number = tierValue.split('_').pop();
      return `EE+${number}`;
    }
    return tierValue;
  };

  const handleNameChange = (tierKey: string, value: string) => {
    setTierNames((prev) => ({
      ...prev,
      [tierKey]: value,
    }));
  };

  const findDuplicateKeys = (values: Record<string, string>): string[] => {
    const seen = new Map<string, string>();
    const duplicates: Set<string> = new Set();

    for (const [key, value] of Object.entries(values)) {
      const normalized = value?.trim().toLowerCase();

      if (!normalized) continue;

      if (seen.has(normalized)) {
        // Add both the original and current duplicate keys
        duplicates.add(key);
        duplicates.add(seen.get(normalized)!);
      } else {
        seen.set(normalized, key);
      }
    }

    return Array.from(duplicates);
  };

  const validateForm = useCallback(() => {
    const values = form.getFieldsValue() as Record<string, string | undefined>;
    const allValues = Object.values<string | undefined>(values);

    // Check for invalid length
    const hasInvalidLength = allValues.some(
      (value) => value !== undefined && value.length > 20
    );

    // Check for duplicates (case-insensitive and ignoring empty strings)
    const nonEmptyValues = allValues.filter((value): value is string =>
      Boolean(value?.trim())
    );
    const valueCounts: Record<string, number> = {};

    nonEmptyValues.forEach((value) => {
      const lowerValue = value.toLowerCase();
      valueCounts[lowerValue] = (valueCounts[lowerValue] || 0) + 1;
    });

    setIsFormValid(!hasInvalidLength);
  }, [form]);

  useEffect(() => {
    if (isOpen) {
      validateForm();
    }
  }, [isOpen, validateForm]);

  const handleSave = async () => {
    if (!masterId || !planType || !tierNames) return;
    try {
      const values = form.getFieldsValue() as Record<string, string>;

      // Find duplicates
      const duplicateKeys = findDuplicateKeys(values);

      if (duplicateKeys.length > 0) {
        const duplicateErrors = duplicateKeys.map((key) => ({
          name: key,
          errors: ['Tier name already exists'],
        }));

        form.setFields(duplicateErrors);
        return;
      }

      const trimmedTierNames = Object.fromEntries(
        Object.entries(tierNames).map(([key, value]) => [key, value?.trim()])
      );
      await updateNtierLabels(masterId, planType.toUpperCase(), {
        ee: trimmedTierNames['EE'],
        eePlusOne: trimmedTierNames['EE_PLUS_1'],
        eePlusTwo: trimmedTierNames['EE_PLUS_2'],
        eePlusThree: trimmedTierNames['EE_PLUS_3'],
        eePlusFour: trimmedTierNames['EE_PLUS_4'],
        eePlusFive: trimmedTierNames['EE_PLUS_5'],
        eePlusSix: trimmedTierNames['EE_PLUS_6'],
      });
      onClose();
      form.resetFields();
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error('Failed to save tier names:', error);
    }
  };

  return (
    <Modal
      visible={isOpen}
      onCancel={onClose}
      footer={null}
      className={styles.modal}
      closable={false}
      maskClosable={false}
      width={552}
    >
      <div className={styles.header}>Edit Tier Names</div>
      <div className={styles.content}>
        <p className={styles.description}>
          Change the name of any tier using the form below. Limit of 20
          characters per tier name.
        </p>

        <Form form={form} layout="vertical">
          <div className={styles.formContainer}>
            {coverages[0].items.slice(0, nTiersCount).map((item) => (
              <div key={item.nTierValue} className={styles.formItem}>
                <Form.Item
                  key={item.nTierValue}
                  className={styles.labelText}
                  label={formatTierForDisplay(item.nTierValue)}
                  name={item.nTierValue}
                  rules={[
                    {
                      max: 20,
                      message: 'Character limit reached',
                      validateTrigger: 'onChange',
                    },
                  ]}
                >
                  <Input
                    value={tierNames[item.nTierValue] || ''}
                    onChange={(e) => {
                      handleNameChange(item.nTierValue, e.target.value);
                      validateForm();
                    }}
                    maxLength={60}
                  />
                </Form.Item>
              </div>
            ))}
          </div>
        </Form>

        <Row justify="end" gutter={16} className={styles.buttons}>
          <Col>
            <Button
              className={styles.cancelButton}
              onClick={() => {
                onClose();
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Col>
          <Col>
            <Button
              className={styles.saveButton}
              type="primary"
              onClick={handleSave}
              disabled={hasEmpty || !isFormValid}
            >
              Save
            </Button>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default TierNameEditor;
